import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Target, ArrowRight, CheckCircle2, Clock, Heart, TrendingUp } from 'lucide-react';

const CareerGoalsStep = ({ profileData, setProfileData, onNext, onSkip }) => {
    const [showFollowUp, setShowFollowUp] = useState(false);

    // Determine currency based on location
    const getCurrency = () => {
        const location = profileData.locationPreference?.toLowerCase() || '';
        if (location.includes('uk') || location.includes('london')) return '£';
        if (location.includes('canada') || location.includes('toronto')) return 'CAD $';
        if (location.includes('australia') || location.includes('sydney')) return 'AUD $';
        if (location.includes('singapore')) return 'SGD $';
        if (location.includes('germany') || location.includes('berlin') || location.includes('europe')) return '€';
        return '$'; // Default to USD
    };

    const currency = getCurrency();

    const careerGoals = [
        {
            id: 'urgent',
            title: 'Need a job ASAP',
            description: 'Looking to start a new position as soon as possible',
            icon: Clock,
            iconColor: 'text-orange-600',
            iconBg: 'bg-orange-100',
            color: 'bg-orange-500',
            borderColor: 'border-orange-400',
            hoverBorder: 'hover:border-orange-500',
            selectedBg: 'bg-orange-50',
            followUp: {
                question: 'Would you like us to prioritize smaller companies?',
                description: 'Companies under 200 employees typically hire faster and are easier to network with.',
                field: 'prioritizeSmallCompanies'
            }
        },
        {
            id: 'better-environment',
            title: 'Want a better work environment',
            description: 'Currently employed but seeking better culture and management',
            icon: Heart,
            iconColor: 'text-pink-600',
            iconBg: 'bg-pink-100',
            color: 'bg-pink-500',
            borderColor: 'border-pink-400',
            hoverBorder: 'hover:border-pink-500',
            selectedBg: 'bg-pink-50',
            followUp: {
                question: 'Prioritize companies with high employee morale?',
                description: 'We\'ll show you jobs at companies with excellent culture ratings and employee satisfaction scores.',
                field: 'prioritizeHighMorale'
            }
        },
        {
            id: 'higher-salary',
            title: 'Want to earn more',
            description: 'Happy with current job but seeking better compensation',
            icon: TrendingUp,
            iconColor: 'text-green-600',
            iconBg: 'bg-green-100',
            color: 'bg-green-500',
            borderColor: 'border-green-400',
            hoverBorder: 'hover:border-green-500',
            selectedBg: 'bg-green-50',
            followUp: {
                question: 'What is your current salary?',
                description: 'We\'ll prioritize opportunities that pay more than your current compensation.',
                field: 'currentSalary',
                isInput: true
            }
        }
    ];

    const selectedGoal = careerGoals.find(g => g.id === profileData.careerGoal);

    const handleGoalSelect = (goalId) => {
        setProfileData({
            ...profileData,
            careerGoal: goalId,
            // Reset follow-up preferences when changing goal
            prioritizeSmallCompanies: undefined,
            prioritizeHighMorale: undefined,
            currentSalary: '',
            prioritizeHigherPay: undefined
        });
        setShowFollowUp(true);
    };

    const handleFollowUpYes = () => {
        if (profileData.careerGoal === 'urgent') {
            setProfileData({ ...profileData, prioritizeSmallCompanies: true });
        } else if (profileData.careerGoal === 'better-environment') {
            setProfileData({ ...profileData, prioritizeHighMorale: true });
        }
        // Auto-proceed after a short delay to show selection
        setTimeout(() => onNext(), 300);
    };

    const handleFollowUpNo = () => {
        if (profileData.careerGoal === 'urgent') {
            setProfileData({ ...profileData, prioritizeSmallCompanies: false });
        } else if (profileData.careerGoal === 'better-environment') {
            setProfileData({ ...profileData, prioritizeHighMorale: false });
        }
        // Auto-proceed after a short delay to show selection
        setTimeout(() => onNext(), 300);
    };

    const handleSalaryInput = (value) => {
        setProfileData({ 
            ...profileData, 
            currentSalary: value,
            prioritizeHigherPay: value ? true : false
        });
    };

    const handleSalarySubmit = () => {
        if (profileData.currentSalary) {
            setTimeout(() => onNext(), 300);
        }
    };

    const canProceed = () => {
        if (!profileData.careerGoal) return false;
        
        // Check if follow-up is answered
        if (profileData.careerGoal === 'urgent') {
            return profileData.prioritizeSmallCompanies !== undefined;
        } else if (profileData.careerGoal === 'better-environment') {
            return profileData.prioritizeHighMorale !== undefined;
        } else if (profileData.careerGoal === 'higher-salary') {
            return profileData.currentSalary !== '';
        }
        
        return true;
    };

    return (
        <div className="max-w-6xl mx-auto px-4">
            {/* Modal Container */}
            <div className="bg-white rounded-3xl shadow-2xl p-8 border border-gray-200 mb-6">
                {/* Header */}
                <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-100 rounded-full mb-4">
                        <Target className="text-blue-700" size={20} />
                        <span className="text-sm font-bold text-blue-900">Personalize Your Experience</span>
                    </div>
                    <h2 className="text-4xl font-bold text-gray-900 mb-3">What's your main career goal?</h2>
                    <p className="text-gray-700 text-lg font-medium">
                        Help us tailor job recommendations and chatbot responses to your priorities
                    </p>
                </div>

            <AnimatePresence mode="wait">
                {!showFollowUp ? (
                    <motion.div
                        key="goal-selection"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -20 }}
                        transition={{ duration: 0.3 }}
                        className="grid md:grid-cols-3 gap-6 mb-6"
                    >
                        {careerGoals.map((goal) => {
                            const isSelected = profileData.careerGoal === goal.id;
                            
                            return (
                                <motion.button
                                    key={goal.id}
                                    type="button"
                                    onClick={() => handleGoalSelect(goal.id)}
                                    whileHover={{ scale: 1.03, y: -6 }}
                                    whileTap={{ scale: 0.98 }}
                                    className={`relative p-6 rounded-2xl bg-white border-3 text-left transition-all duration-300 shadow-lg hover:shadow-2xl ${
                                        isSelected
                                            ? `${goal.borderColor} border-4 ${goal.selectedBg}`
                                            : `border-gray-300 ${goal.hoverBorder}`
                                    }`}
                                >
                                    {/* Selected Check */}
                                    {isSelected && (
                                        <motion.div
                                            initial={{ scale: 0 }}
                                            animate={{ scale: 1 }}
                                            className="absolute top-4 right-4"
                                        >
                                            <CheckCircle2 className="text-blue-600" size={28} />
                                        </motion.div>
                                    )}

                                    {/* Icon Badge */}
                                    <div className="mb-5">
                                        <div className={`w-16 h-16 ${goal.iconBg} rounded-2xl flex items-center justify-center`}>
                                            <goal.icon className={goal.iconColor} size={32} strokeWidth={2.5} />
                                        </div>
                                    </div>

                                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{goal.title}</h3>
                                    <p className="text-base text-gray-700 leading-relaxed font-medium">{goal.description}</p>
                                </motion.button>
                            );
                        })}
                    </motion.div>
                ) : (
                    <motion.div
                        key="follow-up"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                        className="max-w-3xl mx-auto"
                    >
                        {selectedGoal && (
                            <div className={`p-8 rounded-2xl bg-white border-3 ${selectedGoal.borderColor} shadow-xl`}>
                                {/* Goal Summary */}
                                <div className="flex items-start gap-5 mb-6 pb-6 border-b-2 border-gray-200">
                                    <div className={`w-16 h-16 ${selectedGoal.iconBg} rounded-2xl flex items-center justify-center flex-shrink-0`}>
                                        <selectedGoal.icon className={selectedGoal.iconColor} size={32} strokeWidth={2.5} />
                                    </div>
                                    <div>
                                        <h3 className="text-2xl font-bold text-gray-900 mb-2">{selectedGoal.title}</h3>
                                        <p className="text-base text-gray-700 font-medium">{selectedGoal.description}</p>
                                    </div>
                                </div>

                                {/* Follow-up Question */}
                                <div className="space-y-5">
                                    <div>
                                        <h4 className="text-xl font-bold text-gray-900 mb-3">
                                            {selectedGoal.followUp.question}
                                        </h4>
                                        <p className="text-base text-gray-700 leading-relaxed font-medium bg-gray-50 p-4 rounded-xl border-2 border-gray-200">
                                            {selectedGoal.followUp.description}
                                        </p>
                                    </div>

                                    {/* Input for salary goal */}
                                    {selectedGoal.followUp.isInput ? (
                                        <div className="space-y-4 mt-6">
                                            <div className="relative">
                                                <span className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-700 font-bold text-xl">
                                                    {currency}
                                                </span>
                                                <input
                                                    type="number"
                                                    placeholder="e.g., 85000"
                                                    value={profileData.currentSalary}
                                                    onChange={(e) => handleSalaryInput(e.target.value)}
                                                    onKeyPress={(e) => e.key === 'Enter' && handleSalarySubmit()}
                                                    className="w-full pl-14 pr-6 py-5 text-xl font-semibold border-3 border-gray-300 rounded-xl focus:ring-4 focus:ring-green-200 focus:border-green-500 transition-all bg-white"
                                                />
                                            </div>
                                            <p className="text-sm text-gray-600 font-medium bg-green-50 p-3 rounded-lg border-2 border-green-200">
                                                💡 We'll prioritize jobs with salaries higher than this amount
                                            </p>
                                            {profileData.currentSalary && (
                                                <button
                                                    type="button"
                                                    onClick={handleSalarySubmit}
                                                    className="w-full py-5 px-8 rounded-xl font-bold text-lg transition-all shadow-md hover:shadow-xl bg-blue-600 text-white border-3 border-blue-700 hover:bg-blue-700"
                                                >
                                                    Continue with {currency}{parseInt(profileData.currentSalary).toLocaleString()}
                                                </button>
                                            )}
                                        </div>
                                    ) : (
                                        /* Yes/No buttons for other goals */
                                        <div className="flex gap-5 mt-6">
                                            <button
                                                type="button"
                                                onClick={handleFollowUpYes}
                                                className={`flex-1 py-5 px-8 rounded-xl font-bold text-lg transition-all shadow-md hover:shadow-xl ${
                                                    profileData[selectedGoal.followUp.field] === true
                                                        ? 'bg-blue-600 text-white border-3 border-blue-700'
                                                        : 'bg-white border-3 border-gray-300 text-gray-800 hover:border-blue-400'
                                                }`}
                                            >
                                                Yes, prioritize this
                                            </button>
                                            <button
                                                type="button"
                                                onClick={handleFollowUpNo}
                                                className={`flex-1 py-5 px-8 rounded-xl font-bold text-lg transition-all shadow-md hover:shadow-xl ${
                                                    profileData[selectedGoal.followUp.field] === false
                                                        ? 'bg-gray-700 text-white border-3 border-gray-800'
                                                        : 'bg-white border-3 border-gray-300 text-gray-800 hover:border-gray-400'
                                                }`}
                                            >
                                                No, show all jobs
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Change Goal Button */}
                                <button
                                    type="button"
                                    onClick={() => setShowFollowUp(false)}
                                    className="mt-6 text-base text-gray-700 hover:text-gray-900 font-bold underline"
                                >
                                    ← Change my goal
                                </button>
                            </div>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
            </div> {/* End Modal Container */}

            {/* Navigation Buttons - Outside Modal */}
            <div className="flex justify-between items-center mt-6">
                <button
                    type="button"
                    onClick={onSkip}
                    className="px-8 py-4 text-gray-700 hover:text-gray-900 font-bold text-lg transition-colors"
                >
                    Skip for now
                </button>
                
                <button
                    type="button"
                    onClick={onNext}
                    disabled={!canProceed()}
                    className={`px-10 py-5 rounded-xl font-bold text-lg transition-all flex items-center gap-3 shadow-lg ${
                        canProceed()
                            ? 'bg-blue-600 text-white hover:bg-blue-700 hover:shadow-xl hover:scale-105'
                            : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    }`}
                >
                    Continue
                    <ArrowRight size={22} />
                </button>
            </div>
        </div>
    );
};

export default CareerGoalsStep;
